import { HardHat, Plus, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Modal } from "../../../components/Modal";
import { api, rows } from "../../../lib/api";
import type { ApiList, Project } from "../../../lib/types";
import { btpPaths } from "../api";
import { useBtpCapabilities } from "../access";
import type { Chantier } from "../types";

function money(value: unknown, currency = "XOF") {
  return `${Number(value || 0).toLocaleString("fr-FR")} ${currency}`;
}

function badge(status: string) {
  if (status === "ACTIF") return "bg-emerald-100 text-emerald-800";
  if (status === "TERMINE") return "bg-blue-100 text-blue-800";
  if (status === "SUSPENDU") return "bg-amber-100 text-amber-800";
  if (status === "ANNULE") return "bg-red-100 text-red-800";
  return "bg-slate-100 text-slate-700";
}

export function ChantiersPage() {
  const capabilities = useBtpCapabilities();
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [modal, setModal] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setBusy(true);
    try {
      const c = await api.get<ApiList<Chantier>>(btpPaths.chantiers);
      setChantiers(rows(c));
      if (capabilities.manage) {
        const p = await api.get<ApiList<Project>>("/api/v1/projects/projets/?ordering=-created_at");
        setProjects(rows(p));
      }
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createChantier(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    try {
      await api.post(btpPaths.chantiers, {
        code: data.code,
        titre: data.titre,
        adresse: data.adresse,
        description: data.description,
        montant_contrat: data.montant_contrat || "0",
        budget_previsionnel: data.budget_previsionnel || "0",
        statut: data.statut || "PLANIFIE",
        projet: data.projet ? Number(data.projet) : null,
        client_reference: data.client_reference || "",
        contrat_reference: data.contrat_reference || "",
        site_reference: data.site_reference || "",
        date_debut_prevue: data.date_debut_prevue || null,
        date_fin_prevue: data.date_fin_prevue || null,
      });
      setModal(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Création impossible.");
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-amber-700">BTP</div>
          <h1 className="mt-1 text-3xl font-black">Chantiers</h1>
          <p className="mt-2 text-sm text-slate-500">
            Un chantier est la vue métier BTP d’un projet SahelTech. Le moteur projet reste la source
            du planning, des tâches et de l’équipe.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => void load()}>
            <RefreshCw size={16} /> Actualiser
          </button>
          {capabilities.manage && <button className="btn-primary" onClick={() => setModal(true)}>
            <Plus size={16} /> Nouveau chantier
          </button>}
        </div>
      </div>

      {error && <div className="mt-4 rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}

      <div className="panel mt-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Chantier</th>
                <th className="px-5 py-3">Site</th>
                {capabilities.financial_read && <th className="px-5 py-3">Contrat</th>}
                {capabilities.costs && <th className="px-5 py-3">Budget</th>}
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3">Projet</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {chantiers.map((chantier) => (
                <tr key={chantier.id} className="hover:bg-slate-50/70">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 font-bold text-slate-950">
                      <HardHat size={17} className="text-amber-600" />
                      {chantier.code}
                    </div>
                    <div className="mt-1 text-slate-500">{chantier.titre}</div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{chantier.adresse || "—"}</td>
                  {capabilities.financial_read && <td className="px-5 py-4 font-semibold">{money(chantier.montant_contrat, chantier.devise)}</td>}
                  {capabilities.costs && <td className="px-5 py-4">{money(chantier.budget_previsionnel, chantier.devise)}</td>}
                  <td className="px-5 py-4">
                    <span className={`badge ${badge(chantier.statut)}`}>{chantier.statut}</span>
                  </td>
                  <td className="px-5 py-4">
                    {chantier.projet ? (
                      <Link className="font-semibold text-slate-700 underline" to={`/projects/${chantier.projet}`}>
                        Projet #{chantier.projet}
                      </Link>
                    ) : (
                      <span className="text-slate-400">Non lié</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link to={`/btp/chantiers/${chantier.id}`} className="btn-secondary">
                      Ouvrir
                    </Link>
                  </td>
                </tr>
              ))}
              {!busy && !chantiers.length && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-slate-500">
                    Aucun chantier. Créez le premier chantier BTP.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modal} title="Nouveau chantier" onClose={() => setModal(false)} wide>
        <form onSubmit={createChantier} className="grid gap-4 md:grid-cols-2">
          <label>
            <span className="label">Code</span>
            <input name="code" className="field" required placeholder="BTP-2026-001" />
          </label>
          <label>
            <span className="label">Nom du chantier</span>
            <input name="titre" className="field" required />
          </label>
          <label className="md:col-span-2">
            <span className="label">Adresse / site</span>
            <input name="adresse" className="field" />
          </label>
          <label>
            <span className="label">Montant du contrat</span>
            <input name="montant_contrat" className="field" type="number" min="0" step="0.01" defaultValue="0" />
          </label>
          <label>
            <span className="label">Budget prévisionnel</span>
            <input name="budget_previsionnel" className="field" type="number" min="0" step="0.01" defaultValue="0" />
          </label>
          <label>
            <span className="label">Statut</span>
            <select name="statut" className="field" defaultValue="PLANIFIE">
              <option value="PLANIFIE">Planifié</option>
              <option value="ACTIF">Actif</option>
              <option value="SUSPENDU">Suspendu</option>
              <option value="TERMINE">Terminé</option>
            </select>
          </label>
          <label>
            <span className="label">Projet SahelTech lié</span>
            <select name="projet" className="field" defaultValue="">
              <option value="">Aucun pour le moment</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code} · {project.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="label">Référence client</span>
            <input name="client_reference" className="field" />
          </label>
          <label>
            <span className="label">Référence contrat</span>
            <input name="contrat_reference" className="field" />
          </label>
          <label>
            <span className="label">Début prévu</span>
            <input name="date_debut_prevue" className="field" type="date" />
          </label>
          <label>
            <span className="label">Fin prévue</span>
            <input name="date_fin_prevue" className="field" type="date" />
          </label>
          <label className="md:col-span-2">
            <span className="label">Description</span>
            <textarea name="description" className="field min-h-24" />
          </label>
          <div className="flex justify-end gap-2 md:col-span-2">
            <button type="button" className="btn-secondary" onClick={() => setModal(false)}>Annuler</button>
            <button className="btn-primary">Créer le chantier</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
